import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, ChevronLeft, MapPin, Zap, Home, Store, Package, Check, AlertCircle, Phone, PhoneCall } from "lucide-react";
import { parsePhoneNumberFromString, isValidPhoneNumber } from "libphonenumber-js";
import api, { eur, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { getSessionId } from "@/lib/track";
import { getAdRef } from "@/lib/ads";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import ContactModal from "@/components/ContactModal";

const ICONS = { home: Home, "map-pin": MapPin, zap: Zap, store: Store, package: Package };
function MethodIcon({ name }) {
  const C = ICONS[name] || Package;
  return <C className="h-5 w-5" strokeWidth={1.5} />;
}

const COUNTRIES = [
  { code: "FR", label: "France" },
  { code: "BE", label: "Belgique" },
];

const POSTAL_RULES = { FR: /^\d{5}$/, BE: /^\d{4}$/ };
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

function validateForm(form, country) {
  const errors = {};
  if (!form.name || form.name.trim().length < 2) errors.name = "Nom complet requis (2 caractères min)";
  if (!form.email) errors.email = "Email requis";
  else if (!EMAIL_RE.test(form.email.trim())) errors.email = "Format d'email invalide";
  if (!form.phone) errors.phone = "Téléphone requis";
  else {
    try {
      if (!isValidPhoneNumber(form.phone, country)) {
        errors.phone = country === "FR"
          ? "Format invalide (ex: 06 12 34 56 78 ou +33 6 12 34 56 78)"
          : "Format invalide (ex: 0470 12 34 56 ou +32 470 12 34 56)";
      }
    } catch {
      errors.phone = "Format de téléphone invalide";
    }
  }
  if (!form.address || form.address.trim().length < 3) errors.address = "Adresse requise";
  if (!form.city || form.city.trim().length < 2) errors.city = "Ville requise";
  if (!form.postal_code) errors.postal_code = "Code postal requis";
  else if (POSTAL_RULES[country] && !POSTAL_RULES[country].test(form.postal_code.trim())) {
    errors.postal_code = country === "FR" ? "Code postal invalide (5 chiffres)" : "Code postal invalide (4 chiffres)";
  }
  return errors;
}

/* ---------------- Field with validation state ---------------- */
function ValidatedField({ label, error, touched, children, hint, refEl }) {
  const showError = touched && error;
  return (
    <div ref={refEl} className="scroll-mt-32">
      <label className="text-xs uppercase tracking-[0.15em] text-ash">{label}</label>
      <div className={`mt-2 ${showError ? "[&_input]:border-red-500 [&_input]:focus-visible:ring-red-300" : ""}`}>
        {children}
      </div>
      {showError ? (
        <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" /> {error}
        </p>
      ) : (
        hint && <p className="text-[11px] text-ash mt-1">{hint}</p>
      )}
    </div>
  );
}

export default function Checkout() {
  const { items, subtotal, paymentEnabled } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    address: user?.address || "",
    city: user?.city || "",
    postal_code: user?.postal_code || "",
    country: user?.country || "FR",
  });

  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [addressVerified, setAddressVerified] = useState(false);

  // Refs to scroll/focus to the first invalid field on submit
  const fieldRefs = {
    name: useRef(null),
    email: useRef(null),
    phone: useRef(null),
    address: useRef(null),
    city: useRef(null),
    postal_code: useRef(null),
    shipping_method: useRef(null),
  };

  const FIELD_ORDER = ["name", "email", "phone", "address", "city", "postal_code"];
  const FIELD_LABELS = {
    name: "le nom complet",
    email: "l'email",
    phone: "le numéro de téléphone",
    address: "l'adresse",
    city: "la ville",
    postal_code: "le code postal",
    shipping_method: "le mode de livraison",
  };

  const errors = useMemo(() => validateForm(form, form.country), [form]);
  const isValid = Object.keys(errors).length === 0;

  const scrollToField = (key, errorMsg) => {
    const node = fieldRefs[key]?.current;
    if (!node) return;
    try {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch {
      node.scrollIntoView();
    }
    setTimeout(() => {
      try {
        // Find the actual input/select inside the wrapper to focus it
        const input = node.tagName === "INPUT" || node.tagName === "SELECT"
          ? node
          : node.querySelector("input, select, textarea");
        if (input) input.focus({ preventScroll: true });
      } catch { /* ignore */ }
    }, 400);
    if (errorMsg) {
      toast.error(`Vérifiez ${FIELD_LABELS[key] || key} : ${errorMsg}`);
    } else {
      toast.error(`Vérifiez ${FIELD_LABELS[key] || key}.`);
    }
  };

  const [shipOpts, setShipOpts] = useState(null);
  const [methodId, setMethodId] = useState(null);
  const [loadingOpts, setLoadingOpts] = useState(false);

  // Fetch shipping options whenever country or subtotal changes
  useEffect(() => {
    if (!items.length || !form.country) return;
    setLoadingOpts(true);
    api
      .get("/shipping/options", { params: { country: form.country, subtotal } })
      .then(({ data }) => {
        setShipOpts(data);
        if (data.options.length) {
          if (!data.options.find((o) => o.id === methodId)) {
            const cheapest = [...data.options].sort((a, b) => a.price - b.price)[0];
            setMethodId(cheapest.id);
          }
        } else {
          setMethodId(null);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingOpts(false));
  }, [form.country, subtotal, items.length]);

  const selectedOpt = useMemo(
    () => shipOpts?.options?.find((o) => o.id === methodId) || null,
    [shipOpts, methodId]
  );
  const shipping = selectedOpt?.price ?? 0;
  const total = subtotal + shipping;

  const setField = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
    if (k === "address" || k === "city" || k === "postal_code") setAddressVerified(false);
  };

  const markTouched = (k) => () => setTouched((t) => ({ ...t, [k]: true }));
  const shouldShowError = (k) => (touched[k] || submitAttempted) && !!errors[k];

  // Auto-format phone on blur
  const formatPhoneOnBlur = useCallback(() => {
    if (!form.phone) return;
    try {
      const parsed = parsePhoneNumberFromString(form.phone, form.country);
      if (parsed && parsed.isValid()) {
        setForm((f) => ({ ...f, phone: parsed.formatInternational() }));
      }
    } catch {
      /* ignore parse errors */
    }
    setTouched((t) => ({ ...t, phone: true }));
  }, [form.phone, form.country]);

  const pickAddress = (s) => {
    setForm((f) => ({
      ...f,
      address: s.street || s.label || f.address,
      city: s.city || f.city,
      postal_code: s.postal_code || f.postal_code,
      country: s.country_code || f.country,
    }));
    setAddressVerified(true);
    setTouched((t) => ({ ...t, address: true, city: true, postal_code: true }));
  };

  const pay = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    setTouched({ name: true, email: true, phone: true, address: true, city: true, postal_code: true });
    if (items.length === 0) return;
    if (!isValid) {
      // Find first invalid field in display order and scroll to it
      const firstKey = FIELD_ORDER.find((k) => errors[k]);
      if (firstKey) scrollToField(firstKey, errors[firstKey]);
      return;
    }
    if (!methodId) {
      scrollToField("shipping_method", "Sélectionnez un mode de livraison.");
      return;
    }
    setSubmitting(true);
    try {
      const phoneE164 = (() => {
        try {
          const p = parsePhoneNumberFromString(form.phone, form.country);
          return p && p.isValid() ? p.number : form.phone;
        } catch {
          return form.phone;
        }
      })();
      const { data } = await api.post("/checkout/session", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
        shipping: {
          full_name: form.name.trim(),
          email: form.email.trim(),
          phone: phoneE164,
          address: form.address.trim(),
          city: form.city.trim(),
          postal_code: form.postal_code.trim(),
          country: form.country,
        },
        shipping_method_id: methodId,
        session_id: getSessionId(),
        ad_ref: getAdRef(),
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="px-6 md:px-12 py-24 text-center">
        <h1 className="font-serif text-4xl text-ink">Votre panier est vide</h1>
        <Link to="/boutique" className="inline-block mt-6 bg-ink text-cream px-8 py-4 text-xs uppercase tracking-[0.15em]">
          Découvrir la boutique
        </Link>
      </div>
    );
  }

  return (
    <div className="px-6 md:px-12 pt-8 md:pt-12 max-w-6xl mx-auto">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-ash hover:text-ink mb-6">
        <ChevronLeft className="h-4 w-4" /> Retour
      </button>
      <h1 className="font-serif text-4xl md:text-5xl text-ink tracking-tight mb-10">Finaliser ma commande</h1>

      <div className="grid lg:grid-cols-2 gap-12">
        <form onSubmit={pay} className="space-y-8 order-2 lg:order-1" noValidate>
          {/* Contact */}
          <section>
            <h2 className="font-serif text-2xl text-ink mb-5">Vos coordonnées</h2>
            {!user && (
              <p className="text-sm text-graphite bg-sand p-4 rounded-sm mb-4">
                Vous avez un compte ?{" "}
                <Link to="/connexion" className="underline underline-offset-2 text-ink">Connectez-vous</Link>
                {" "}pour suivre vos commandes.
              </p>
            )}
            <div className="space-y-5">
              <ValidatedField refEl={fieldRefs.name} label="Nom complet *" error={errors.name} touched={shouldShowError("name")}>
                <Input data-testid="checkout-name" value={form.name} onChange={setField("name")} onBlur={markTouched("name")} placeholder="Jean Dupont" className="rounded-sm border-stone bg-white h-12" />
              </ValidatedField>
              <ValidatedField refEl={fieldRefs.email} label="Email *" error={errors.email} touched={shouldShowError("email")} hint="Pour recevoir la confirmation de commande.">
                <Input data-testid="checkout-email" type="email" inputMode="email" value={form.email} onChange={setField("email")} onBlur={markTouched("email")} placeholder="jean@email.com" className="rounded-sm border-stone bg-white h-12" />
              </ValidatedField>
              <ValidatedField refEl={fieldRefs.phone} label="Téléphone *" error={errors.phone} touched={shouldShowError("phone")} hint={form.country === "FR" ? "Pour le transporteur (ex: 06 12 34 56 78)" : "Pour le transporteur (ex: 0470 12 34 56)"}>
                <div className="relative">
                  <Phone className="h-4 w-4 text-ash absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    data-testid="checkout-phone"
                    type="tel"
                    inputMode="tel"
                    value={form.phone}
                    onChange={setField("phone")}
                    onBlur={formatPhoneOnBlur}
                    placeholder={form.country === "FR" ? "+33 6 12 34 56 78" : "+32 470 12 34 56"}
                    className="rounded-sm border-stone bg-white h-12 pl-10"
                  />
                </div>
              </ValidatedField>
            </div>
          </section>

          {/* Address */}
          <section>
            <h2 className="font-serif text-2xl text-ink mb-5">Adresse de livraison</h2>
            <div className="space-y-5">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-ash">Pays *</label>
                <select
                  data-testid="checkout-country"
                  value={form.country}
                  onChange={(e) => { setForm((f) => ({ ...f, country: e.target.value })); setAddressVerified(false); }}
                  className="mt-2 w-full rounded-sm border border-stone bg-white h-12 px-3"
                >
                  {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                </select>
              </div>
              <ValidatedField
                refEl={fieldRefs.address}
                label="Adresse *"
                error={errors.address}
                touched={shouldShowError("address")}
                hint={addressVerified ? "✓ Adresse vérifiée" : "Tapez votre adresse, puis sélectionnez une suggestion pour la valider."}
              >
                <AddressAutocomplete
                  country={form.country}
                  value={form.address}
                  onChange={(v) => setField("address")(v)}
                  onPick={pickAddress}
                  verified={addressVerified}
                  error={shouldShowError("address") ? errors.address : ""}
                  testid="checkout-address"
                  placeholder={form.country === "FR" ? "12 rue de la Paix, Paris" : "Avenue Louise, Bruxelles"}
                />
              </ValidatedField>
              <div className="grid grid-cols-2 gap-4">
                <ValidatedField refEl={fieldRefs.city} label="Ville *" error={errors.city} touched={shouldShowError("city")}>
                  <Input data-testid="checkout-city" value={form.city} onChange={setField("city")} onBlur={markTouched("city")} placeholder="Paris" className="rounded-sm border-stone bg-white h-12" />
                </ValidatedField>
                <ValidatedField refEl={fieldRefs.postal_code} label="Code postal *" error={errors.postal_code} touched={shouldShowError("postal_code")}>
                  <Input data-testid="checkout-postal" inputMode="numeric" value={form.postal_code} onChange={setField("postal_code")} onBlur={markTouched("postal_code")} placeholder={form.country === "FR" ? "75001" : "1000"} className="rounded-sm border-stone bg-white h-12" />
                </ValidatedField>
              </div>
            </div>
          </section>

          {/* Shipping method */}
          <section ref={fieldRefs.shipping_method} className="scroll-mt-32">
            <h2 className="font-serif text-2xl text-ink mb-5">Mode de livraison</h2>
            {loadingOpts ? (
              <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-16 bg-sand animate-pulse rounded-sm" />)}</div>
            ) : !shipOpts || shipOpts.options.length === 0 ? (
              <p className="text-sm text-graphite bg-sand p-4 rounded-sm">
                Aucune méthode de livraison n&apos;est disponible pour ce pays.
              </p>
            ) : (
              <div className="space-y-2" data-testid="shipping-options">
                {shipOpts.options.map((o) => (
                  <label
                    key={o.id}
                    data-testid={`shipping-opt-${o.id}`}
                    className={`flex items-center gap-3 p-4 border rounded-sm cursor-pointer transition-colors ${
                      methodId === o.id ? "border-copper bg-sand/40" : "border-stone hover:border-graphite"
                    }`}
                  >
                    <input type="radio" name="ship_method" checked={methodId === o.id} onChange={() => setMethodId(o.id)} className="accent-copper" />
                    <span className="p-2 bg-white border border-stone rounded-sm text-copper">
                      <MethodIcon name={o.icon} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-ink">{o.label}</p>
                      <p className="text-xs text-graphite mt-0.5">
                        {o.description}{o.description && o.delivery_time && " · "}{o.delivery_time}
                      </p>
                    </div>
                    <span className="font-mono text-ink">{o.free ? <span className="text-green-700">Offerte</span> : eur(o.price)}</span>
                  </label>
                ))}
              </div>
            )}
          </section>

          {paymentEnabled ? (
            <button
              data-testid="pay-button"
              type="submit"
              disabled={submitting || !methodId}
              className="w-full bg-ink text-cream py-4 uppercase tracking-[0.15em] text-xs hover:bg-gold hover:text-ink transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Lock className="h-4 w-4" />
              {submitting ? "Redirection vers Stripe…" : `Payer ${eur(total)}`}
            </button>
          ) : (
            <button
              data-testid="contact-button"
              type="button"
              onClick={() => setContactOpen(true)}
              className="w-full bg-ink text-cream py-4 uppercase tracking-[0.15em] text-xs hover:bg-gold hover:text-ink transition-colors flex items-center justify-center gap-2"
            >
              <PhoneCall className="h-4 w-4" />
              Contacter
            </button>
          )}
          {submitAttempted && !isValid && (
            <div data-testid="form-errors-summary" className="text-xs bg-red-50 border border-red-200 rounded-sm p-3">
              <p className="text-red-700 font-medium flex items-center gap-1.5 mb-1.5">
                <AlertCircle className="h-4 w-4" /> Merci de corriger les erreurs suivantes :
              </p>
              <ul className="text-red-700 space-y-0.5 pl-5 list-disc">
                {FIELD_ORDER.filter((k) => errors[k]).map((k) => (
                  <li key={k}>
                    <button
                      type="button"
                      onClick={() => scrollToField(k, errors[k])}
                      className="underline underline-offset-2 hover:no-underline text-left"
                    >
                      {errors[k]}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-xs text-ash text-center">
            {paymentEnabled ? "Paiement sécurisé via Stripe. Mode test." : "Le paiement en ligne est momentanément indisponible."}
          </p>
        </form>

        {/* Summary */}
        <div className="order-1 lg:order-2">
          <div className="bg-sand rounded-sm p-6 lg:sticky lg:top-28">
            <h2 className="font-serif text-2xl text-ink mb-5">Récapitulatif</h2>
            <div className="space-y-4 max-h-72 overflow-y-auto pr-1">
              {items.map((i) => (
                <div key={i.product_id} className="flex gap-3" data-testid={`summary-item-${i.slug}`}>
                  <img src={i.image} alt={i.name} className="h-16 w-14 object-cover bg-stone rounded-sm" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-ink leading-snug">{i.name}</p>
                    <p className="text-xs text-ash mt-0.5">Qté {i.quantity}</p>
                  </div>
                  <span className="font-mono text-sm text-ink">{eur(i.price * i.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-stone mt-5 pt-5 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-graphite">Sous-total</span>
                <span className="font-mono text-ink">{eur(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-graphite">
                  Livraison{selectedOpt && <span className="text-ash"> · {selectedOpt.label}</span>}
                </span>
                <span className="font-mono text-ink">{shipping === 0 ? (selectedOpt ? "Offerte" : "—") : eur(shipping)}</span>
              </div>
              {selectedOpt?.delivery_time && (
                <p className="text-xs text-ash flex items-center gap-1">
                  <Check className="h-3 w-3 text-copper" /> {selectedOpt.delivery_time}
                </p>
              )}
              <div className="flex justify-between text-lg pt-2 border-t border-stone mt-2">
                <span className="font-serif text-ink">Total</span>
                <span className="font-mono text-ink" data-testid="checkout-total">{eur(total)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ContactModal
        open={contactOpen}
        onOpenChange={setContactOpen}
        items={items}
        subtotal={subtotal}
        defaults={{ name: form.name, email: form.email, phone: form.phone }}
      />
    </div>
  );
}
